import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { actionBody } from '~/modules/helpers';

import { STATUS } from '~/literals';

import { LocationState, TravelpayoutsLocation } from '~/types';

interface GetLocationsSuccessMeta {
  cached: boolean;
  query: string;
  updatedAt: number;
}

export const locationsState: LocationState = {
  locations: {},
  travelpayoutsLocations: {},
  travelpayoutsLocationsStatus: STATUS.IDLE,
  travelpayoutsLocationsErrorMessage: '',
};

export const locationsSlice = createSlice({
  name: 'locations',
  initialState: locationsState,
  reducers: {
    getLocations: (draft, { payload }: PayloadAction<string>) => {
      draft.locations[payload] = draft.locations[payload] || { ...location };

      draft.locations[payload].message = '';
      draft.locations[payload].status = STATUS.RUNNING;
    },
    getLocationsSuccess: {
      reducer: (
        draft,
        { meta, payload }: PayloadAction<Record<string, any>[], string, GetLocationsSuccessMeta>,
      ) => {
        const { cached = false, query = '', updatedAt = 0 } = meta || {};

        draft.locations[query] = draft.locations[query] || { ...location };

        draft.locations[query].cached = cached;
        draft.locations[query].data = payload;
        draft.locations[query].status = STATUS.SUCCESS;
        draft.locations[query].updatedAt = updatedAt;
      },
      prepare: (payload: Record<string, any>[], meta: GetLocationsSuccessMeta) =>
        actionBody(payload, meta),
    },
    getLocationsFailure: {
      reducer: (draft, { meta, payload }: PayloadAction<string, string, { query: string }>) => {
        const { query = '' } = meta || {};

        draft.locations[query] = draft.locations[query] || { ...location };

        draft.locations[query].message = payload;
        draft.locations[query].status = STATUS.ERROR;
        draft.locations[query].updatedAt = 0;
      },
      prepare: (payload: string, query: string) => actionBody(payload, { query }),
    },
    getTravelpayoutsLocations: (draft, _: PayloadAction<string[]>) => {
      draft.travelpayoutsLocationsStatus = STATUS.RUNNING;
      draft.travelpayoutsLocationsErrorMessage = '';
    },
    getTravelpayoutsLocationsSuccess: (draft, { payload }: PayloadAction<Record<string, TravelpayoutsLocation | null>>) => {
      draft.travelpayoutsLocations = {
        ...draft.travelpayoutsLocations,
        ...payload,
      };
      draft.travelpayoutsLocationsStatus = STATUS.SUCCESS;
      draft.travelpayoutsLocationsErrorMessage = '';
    },
    getTravelpayoutsLocationsFailure: (draft, { payload }: PayloadAction<{ codes: string[]; errorMessage: string }>) => {
      draft.travelpayoutsLocations = payload.codes.reduce<Record<string, TravelpayoutsLocation | null>>((acc, code) => ({
        ...acc,
        [code]: null,
      }), draft.travelpayoutsLocations);
      draft.travelpayoutsLocationsStatus = STATUS.ERROR;
      draft.travelpayoutsLocationsErrorMessage = payload.errorMessage;
    },
  },
});

export const {
  getLocations,
  getLocationsFailure,
  getLocationsSuccess,
  getTravelpayoutsLocations,
  getTravelpayoutsLocationsFailure,
  getTravelpayoutsLocationsSuccess,
} = locationsSlice.actions;
export default locationsSlice.reducer;
